import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

/**
 * Buyer wallet - "Payments".
 *
 * A buyer's relationship with money on Souqli is the opposite of a seller's.
 * A seller is owed money and wants it out; a buyer has spent money and wants to
 * know it's safe, what came back, and what membership saved them. Showing a
 * buyer a payout panel and a withdraw button was showing them somebody else's
 * screen - every control on it was inert.
 *
 * So this page answers a buyer's three real questions:
 *   1. Is my money safe right now?      → held in protection
 *   2. Did I get my refunds?            → refunds
 *   3. What is membership saving me?    → fees waived + voucher
 */
interface BuyerWalletData {
  pendingHoldGBP: number;
  buyer?: {
    creditGBP: number;
    lifetimeSpentGBP: number;
    refundsGBP: number;
    protectionFeesGBP: number;
    ordersInEscrow: number;
    member: boolean;
    voucher: { availableGBP: number; expiresAt: string | null };
  };
  transactions: { id: string; date: string; description: string; amountGBP: number; type: string }[];
}

const TYPE_LABEL: Record<string, { label: string; cls: string }> = {
  PURCHASE: { label: 'Purchase',  cls: 'bg-warmline text-mute' },
  REFUND:   { label: 'Refund',    cls: 'bg-ok/15 text-ok-dark' },
  FEE:      { label: 'Fee',       cls: 'bg-clay/15 text-clay-dark' },
  CREDIT:   { label: 'Credit',    cls: 'bg-gold/15 text-gold-dark' },
  TOPUP:    { label: 'Credit',    cls: 'bg-gold/15 text-gold-dark' },
};

export default function BuyerWallet() {
  const [filter, setFilter] = useState<'all' | 'purchases' | 'refunds'>('all');

  const { data } = useQuery<BuyerWalletData>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get('/me/wallet')).data,
    retry: false,
  });

  const b = data?.buyer;
  const held = data?.pendingHoldGBP ?? 0;
  const txs = data?.transactions ?? [];

  const filtered = txs.filter((t) => {
    if (filter === 'purchases') return t.amountGBP < 0 && t.type !== 'FEE';
    if (filter === 'refunds') return t.type === 'REFUND';
    return true;
  });

  function downloadCsv() {
    if (!txs.length) return toast('No payments to export yet.', { icon: 'ℹ️' });
    const rows = [
      ['Date', 'Description', 'Type', 'Amount (GBP)'],
      ...txs.map((t) => [
        new Date(t.date).toLocaleDateString(),
        `"${(t.description ?? '').replace(/"/g, "'")}"`,
        t.type,
        t.amountGBP.toFixed(2),
      ]),
    ];
    const url = URL.createObjectURL(new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `souqli-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {/*
        One hero carrying both figures. They answer opposite questions and
        belong side by side: what is held (buyer protection) versus what you can
        spend right now (credit). As separate panels they read as competing
        headlines; together they read as one account.
      */}
      <section className="section-dark relative overflow-hidden rounded-luxe text-cream" style={{ background: '#221A12' }}>
        <div className="absolute inset-0 bg-gold-mesh opacity-40" />
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-25" />

        <div className="relative px-5 py-7 sm:px-7 sm:py-9 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Money held in protection */}
          <div className="lg:col-span-12">
            <div className="stamp text-gold text-[9px]">PROTECTED BY SOUQLI RIGHT NOW</div>
            <div className="display text-5xl md:text-6xl mt-2 leading-none">£{held.toFixed(2)}</div>
            <p className="text-[13px] text-mute-soft mt-3 max-w-[52ch] leading-relaxed">
              {b?.ordersInEscrow
                ? `Held across ${b.ordersInEscrow} active order${b.ordersInEscrow === 1 ? '' : 's'}. Souqli keeps your money until you confirm the item arrived as described - the seller isn't paid before that.`
                : 'Nothing in transit. When you buy, your payment is held here until you confirm the item arrived as described.'}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/dashboard/orders" className="btn btn-gold !text-ink">Track my orders →</Link>
              <Link to="/c/all" className="btn text-cream border border-cream/30">Keep shopping</Link>
            </div>
          </div>

        </div>
      </section>

      {/* Four figures a buyer can act on. No payout method, no withdraw. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Total spent"      value={`£${(b?.lifetimeSpentGBP ?? 0).toFixed(2)}`} note="Across all orders" />
        <Tile label="Refunded to you"  value={`£${(b?.refundsGBP ?? 0).toFixed(2)}`}      note="Back to your card" />
        <Tile label="Protection fees"  value={`£${(b?.protectionFeesGBP ?? 0).toFixed(2)}`} note={b?.member ? 'Waived on member orders' : 'Members pay less'} />
        <Tile
          label="Delivery voucher"
          value={`£${(b?.voucher.availableGBP ?? 0).toFixed(2)}`}
          note={b?.voucher.expiresAt
            ? `Expires ${new Date(b.voucher.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
            : 'Earn £4 on £300+ spend'}
          highlight={(b?.voucher.availableGBP ?? 0) > 0}
        />
      </div>

      {/* Membership: a saving, not a subscription pitch, when they already pay. */}
      {b?.member ? (
        <div className="rounded-luxe border border-gold/30 bg-gold/5 p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="stamp text-[9px] text-gold-dark">SOUQLI MEMBER</div>
            <p className="text-[14px] mt-1">
              You’re paying no buyer protection fee on eligible orders, and you get one hour’s early access to new drops.
            </p>
          </div>
          <Link to="/dashboard/membership" className="btn btn-outline btn-sm shrink-0">Manage membership</Link>
        </div>
      ) : (
        <div className="rounded-luxe border border-warmline bg-white p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="stamp text-[9px] text-mute">MEMBERSHIP</div>
            <p className="text-[14px] mt-1 max-w-[58ch] leading-relaxed">
              {b && b.protectionFeesGBP > 0
                ? `You've paid £${b.protectionFeesGBP.toFixed(2)} in buyer protection fees. Membership waives them on eligible orders.`
                : 'Members pay no buyer protection fee on eligible orders, get early access to new drops, and earn a £4 delivery voucher.'}
            </p>
          </div>
          <Link to="/dashboard/membership" className="btn btn-primary btn-sm shrink-0">See membership</Link>
        </div>
      )}

      {/* Payment history - receipts, not a ledger. */}
      <section className="bg-white rounded-luxe border border-warmline overflow-hidden">
        <div className="px-4 sm:px-5 py-4 border-b border-warmline flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-sm text-ink">Payment history</h2>
            <p className="text-[11px] text-mute mt-0.5">Purchases, refunds and fees on your account.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-cream-deep rounded-full p-1">
              {(['all', 'purchases', 'refunds'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3.5 py-1.5 text-[12px] rounded-full transition capitalize ${
                    filter === f ? 'bg-ink text-cream' : 'text-mute hover:text-ink'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <button onClick={downloadCsv} className="btn btn-outline btn-sm">Export</button>
          </div>
        </div>

        {filtered.length ? (
          // Horizontal scroll beats four crushed columns on a phone.
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <tbody>
              {filtered.map((t) => {
                const meta = TYPE_LABEL[t.type] ?? { label: t.type.toLowerCase(), cls: 'bg-warmline text-mute' };
                return (
                  <tr key={t.id} className="border-t border-warmline">
                    <td className="px-5 py-3 text-[12px] text-mute whitespace-nowrap">
                      {new Date(t.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-2 py-3 text-[13px]">{t.description}</td>
                    <td className="px-2 py-3">
                      <span className={`pill text-[9px] ${meta.cls}`}>{meta.label.toUpperCase()}</span>
                    </td>
                    <td className={`px-5 py-3 text-right mono text-[13px] whitespace-nowrap ${t.amountGBP > 0 ? 'text-ok-dark' : 'text-ink'}`}>
                      {t.amountGBP > 0 ? '+' : ''}£{Math.abs(t.amountGBP).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="px-5 py-14 text-center text-mute text-[13px]">
            {txs.length ? 'Nothing matches this filter.' : 'No payments yet. Your purchases and refunds will appear here.'}
          </div>
        )}
      </section>

      <p className="text-[12px] text-mute text-center leading-relaxed max-w-[64ch] mx-auto">
        Refunds go back to the card you paid with, which usually takes 3-5 business days.
        Souqli never stores your card details; payments are handled by Stripe.
      </p>
    </div>
  );
}

function Tile({ label, value, note, highlight }: { label: string; value: string; note: string; highlight?: boolean }) {
  return (
    <div className={`rounded-luxe border p-4 ${highlight ? 'border-gold/40 bg-gold/5' : 'border-warmline bg-white'}`}>
      <div className="stamp text-[9px] text-mute">{label.toUpperCase()}</div>
      <div className="display text-2xl mt-1">{value}</div>
      <div className="text-[11px] text-mute mt-1 leading-snug">{note}</div>
    </div>
  );
}
